import { Component, inject } from '@angular/core';
import {
  ButtonClickEvent,
  CacBaseDialogComponent,
  CacDialogLayoutComponent,
  CacFormBuilderComponent,
  formBuilder,
} from '@controladad/ng-base';
import { formControl, Validators } from '@al00x/forms';
import { OrderApiService } from '../../../services';
import { OrderItemDetail } from '@models';
import { DialogExtendedService } from '../dialog-extended.service';

export interface OrderItemEditDialogData {
  orderItem: OrderItemDetail;
  orderId: number;
  shouldPromptWalletRefund?: boolean;
}

export type OrderItemEditDialogResult = boolean;

@Component({
  selector: 'lib-order-item-edit-dialog',
  imports: [CacDialogLayoutComponent, CacFormBuilderComponent],
  templateUrl: './order-item-edit-dialog.component.html',
  styleUrl: './order-item-edit-dialog.component.scss',
})
export class OrderItemEditDialogComponent extends CacBaseDialogComponent<
  OrderItemEditDialogData,
  OrderItemEditDialogResult
> {
  private readonly orderApi = inject(OrderApiService);
  private readonly dialogService = inject(DialogExtendedService);

  form = formBuilder({
    cols: 1,
    inputs: {
      productName: {
        control: formControl<string>({ value: '', disabled: true }),
        label: 'Product Name',
      },
      quantity: {
        control: formControl<number>(1, [Validators.required, Validators.min(1)]),
        label: 'Quantity',
      },
      overridePrice: {
        control: formControl<number | null>(null, [Validators.min(0)]),
        label: 'Override Item Price',
        suffix: 'KWD'
      },
    },
  });

  constructor() {
    super();
    const orderItem = this.data.orderItem;

    if (orderItem) {
      // Get product name from the order item
      const productName = this.getProductName(orderItem);
      
      this.form.patchValue({
        productName: productName,
        quantity: orderItem.orderedQuantity,
        overridePrice: orderItem.productPrice,
      });
    }
  }

  onSubmit(e: ButtonClickEvent) {
    const value = this.form.getValue();
    const orderItem = this.data.orderItem;
    const orderId = this.data.orderId;
    const shouldPromptWalletRefund = this.data.shouldPromptWalletRefund ?? true;

    if (!value) {
      e.setLoading(false);
      return;
    }

    // Determine the unit price to use
    const unitPrice = value.overridePrice ?? orderItem.productPrice ?? 0;

    // Check if we should prompt for wallet refund
    if (!shouldPromptWalletRefund) {
      // Don't prompt: just update without refund option
      this.updateOrderItemRequest(orderId, orderItem.variantId, value.quantity!, unitPrice, false, false, e);
    } else {
      // Prompt user: show refund confirmation dialog when editing order items
      this.dialogService.refundConfirmation().afterClosed().subscribe((refundToWallet) => {
        if (refundToWallet === true) {
          // User chose "Yes, Refund to Wallet"
          this.updateOrderItemRequest(orderId, orderItem.variantId, value.quantity!, unitPrice, true, false, e);
        } else if (refundToWallet === false) {
          // User chose "No, Just Update"
          this.updateOrderItemRequest(orderId, orderItem.variantId, value.quantity!, unitPrice, false, false, e);
        } else {
          // User closed the dialog (refundToWallet === null)
          e.setLoading(false);
        }
      });
    }
  }

  private updateOrderItemRequest(
    orderId: number,
    variantId: number,
    quantity: number,
    unitPrice: number,
    refundToWallet: boolean,
    forceUpdate: boolean,
    e: ButtonClickEvent
  ): void {
    this.orderApi.updateOrderItem({
      orderId: orderId,
      variantId: variantId,
      quantity: quantity,
      unitPrice: unitPrice,
      variantOrderState: 1,
      refundToWallet: refundToWallet,
      forceUpdate: forceUpdate,
    }).pipe(e.pipe()).subscribe({
      next: () => {
        snackbar$.show('Order item updated successfully');
        this.submit(true);
      },
      error: (error) => {
        // Check if error code is 78 (stock not available)
        const errorCode = error?.error?.code || error?.code;
        if (errorCode === 78 && !forceUpdate) {
          dialog$.prompt({
            message: 'The requested quantity exceeds available stock. Do you want to proceed anyway? This will result in negative stock.',
            title: 'Stock Not Available',
            yesButtonText: 'Yes, Proceed',
            noButtonText: 'Cancel',
          }).action(() =>
            this.orderApi.updateOrderItem({
              orderId: orderId,
              variantId: variantId,
              quantity: quantity,
              unitPrice: unitPrice,
              variantOrderState: 1,
              refundToWallet: refundToWallet,
              forceUpdate: true,
            })
          ).subscribe({
            next: () => {
              snackbar$.show('Order item updated successfully');
              this.submit(true);
            },
            error: () => {
              e.setLoading(false);
            }
          });
        } else {
          e.setLoading(false);
        }
      }
    });
  }

  private getProductName(item: OrderItemDetail): string {
    if (!item.productName) return '-';
    if (typeof item.productName === 'string') return item.productName;
    if (typeof item.productName === 'object') {
      return item.productName.english || item.productName.arabic || '-';
    }
    return '-';
  }
}